import { buildAxiosFetch } from '@lifeomic/axios-fetch'
import axios, { type AxiosRequestConfig } from 'axios'
import type { KeyPairHex } from 'sm-crypto'
import { sm2 } from 'sm-crypto'

import type {
  ClientResponse,
  ConfigNodeArgs,
  ExecuteContractArgs,
  ExecuteContractResponse,
  ListAllUsersResponseData,
  ListNodesResponse,
  ListProjectPermissionRequest,
  ListProjectPermissionResponseData,
  LoadNodeConfigResponseData,
  PingResponse,
  SaveFileRequest,
  StartContractByYpkRequest,
} from './types'

const genUrlParamsFromObject = (obj: Record<string, unknown>): string => {
  return Object.entries(obj)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join('&')
}

export * from './types'

export const retryAsyncN =
  (maxRetries: number) =>
  async <Ret>(fn: () => Promise<Ret>): Promise<Ret> => {
    let i = 0
    while (true) {
      try {
        return await fn()
      } catch (e) {
        i++
        if (i === maxRetries) {
          throw e
        }
      }
    }
  }

export interface HttpClientOptions {
  axiosConfig?: AxiosRequestConfig
  maxRetries?: number
}

export class HttpClient {
  private fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>
  private retryAsync: <Ret>(fn: () => Promise<Ret>) => Promise<Ret>

  constructor(
    private baseUrl: string,
    private sm2Key: KeyPairHex,
    { axiosConfig = {}, maxRetries = 3 }: HttpClientOptions = {},
  ) {
    const axiosInstance = axios.create({
      baseURL: 'https://some-domain.com/api/',
      timeout: 10000,
      // headers: {'X-Custom-Header': 'foobar'}
      ...axiosConfig,
    })

    this.fetch = buildAxiosFetch(
      axiosInstance /* , function (config) {
      config.timeout = 1000;
      return config;
    } */,
    )

    this.retryAsync = retryAsyncN(maxRetries)
  }

  async requestWithSignature<Data>(
    path: string,
    init?: Partial<RequestInit>,
    sm2Key?: KeyPairHex,
  ): Promise<ClientResponse<Data>> {
    return new Promise<ClientResponse<Data>>((resolve, reject) => {
      const rawUrl = this.baseUrl + path
      const url = `${rawUrl}${path.includes('?') ? '&' : '?'}pubKey=${
        sm2Key?.publicKey ?? this.sm2Key.publicKey
      }`

      // console.log('privateKey:', this.sm2Key.privateKey)

      // console.log('publicKey:', this.sm2Key.publicKey)

      // console.log('to sign:', url.substring(url.indexOf('?') + 1))

      const sign = this.sign(
        url.substring(url.indexOf('?') + 1),
        sm2Key?.privateKey,
      )
      // console.log(
      //   'url:',
      //   init?.method?.toLowerCase() === 'post'
      //     ? rawUrl
      //     : `${encodeURI(url)}&sign=${sign}`,
      // )

      this.fetch(
        init?.method?.toLowerCase() === 'post'
          ? rawUrl
          : `${encodeURI(url)}&sign=${sign}`,
        {
          ...init,
          ...(init?.method === 'POST'
            ? {
                body: {
                  ...(init?.body as unknown as Record<string, string>),
                  sign,
                } as unknown as Request['body'],
              }
            : {}),
        },
      )
        .then(async (res) => {
          // console.log(res.url,'请求成功')
          const clientRes = res as unknown as ClientResponse<Data>
          clientRes.data = (await res.text()) as Data
          // Workaround for the issue causing the 2nd consecutive request to fail:
          // https://github.com/nodejs/undici/issues/1415
          setTimeout(() => {
            resolve(clientRes)
          }, 1)
        })
        .catch((err) => {
          // const clientRes = err as unknown as ClientResponse<Data>
          // clientRes.data = (await err.text()) as Data
          setTimeout(() => {
            reject(err)
          }, 1)
        })
    })
  }

  sign(data: string, privateKey?: string): string {
    try {
      return sm2.doSignature(data, privateKey ?? this.sm2Key.privateKey, {
        hash: true,
        der: true,
      })
    } catch (e) {
      throw new Error(
        'Signature failed, please check the configuration information.',
      )
    }
  }

  // ping
  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html#id15
  ping(): Promise<PingResponse> {
    return this.retryAsync(() =>
      this.requestWithSignature('/SCManager?action=ping'),
    )
  }

  // 启动合约
  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html#id60
  startContract(code: string): Promise<ClientResponse<string>> {
    const params = { action: 'startContract', script: code }
    return this.retryAsync(() =>
      this.requestWithSignature(`/SCManager?${genUrlParamsFromObject(params)}`),
    )
  }

  // 启动合约
  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html#id13
  startContractByYPK(
    _request: StartContractByYpkRequest,
  ): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject({
          action: 'startContractByYPK',
          ..._request,
          owner: this.sm2Key.publicKey,
          aim: 'onStartContract',
          signature: sm2.doSignature(
            `Fixed|${_request.path}|${this.sm2Key.publicKey}`,
            this.sm2Key.privateKey,
            {
              hash: true,
              der: true,
            },
          ),
        })}`,
      ),
    )
  }

  // 调用合约
  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html#id69
  executeContract(
    contractID: string,
    operation: string,
    arg: string,
    {
      method = 'POST',
      withDynamicAnalysis = false,
      withSignature = false,
    } = {} as ExecuteContractArgs,
  ): Promise<ClientResponse<ExecuteContractResponse<string>>> {
    let request = {}
    if (withDynamicAnalysis === undefined) {
      request = {
        action: 'executeContract',
        contractID,
        operation,
        arg,
      }
    } else {
      request = {
        action: 'executeContract',
        contractID,
        operation,
        withDynamicAnalysis,
        arg,
      }
    }
    if (withSignature) {
      request = {
        ...request,
        pubkey: this.sm2Key.publicKey,
        signature: this.sign(
          `${contractID}|${operation}|${arg ?? ''}|${this.sm2Key.publicKey}`,
        ),
      }
    }
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager${
          method === 'GET' ? `?${genUrlParamsFromObject(request)}` : ''
        }`,
        {
          method,
          // eslint-disable-next-line  @typescript-eslint/consistent-type-assertions
          ...(method === 'POST' ? ({ body: request } as Request) : {}),
        },
      ),
    )
  }

  // 停止合约
  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html#id122
  killContractProcess(
    contractID: string,
    requestID?: string,
  ): Promise<ClientResponse<string>> {
    const _request: Record<string, string> = {
      action: 'killContractProcess',
      id: contractID,
    }
    requestID && (_request.requestID = requestID!)
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject(_request)}`,
      ),
    )
  }

  // 停止所有合约
  // 地址 http://39.104.205.122:18010/SCIDE
  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html#id131
  killAllContract(): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature('/SCManager?action=killAllContract'),
    )
  }

  // 申请角色
  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html
  applyNodeRole(
    role: string,
  ): Promise<ClientResponse<{ action: string; data: string; role?: string }>> {
    const _request = {
      action: 'applyNodeRole',
      role,
    }
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject(_request)}`,
      ),
    )
  }

  // 授权角色
  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html
  authNodeRole(
    isAccept: boolean,
    authorizedPubKey: string,
    managerPair?: KeyPairHex,
  ): Promise<ClientResponse<{ action: string; data: string }>> {
    const _request = {
      action: 'authNodeRole',
      isAccept,
      authorizedPubKey,
    }
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject(_request)}`,
        undefined,
        managerPair,
      ),
    )
  }

  // https://public.internetapi.cn/docs/bdcontract/doc/ContractAPI.html
  // 用sse获取   问题未解决！！！
  distributeContract(
    nodeIDs: string,
    projectName: string,
    isPrivate: boolean,
  ): void {
    const signature = sm2.doSignature(
      `DistributeContract|${projectName}|${this.sm2Key.publicKey}`,
      this.sm2Key.privateKey,
      { hash: true, der: true },
    )

    const _request = {
      action: 'distributeContract',
      nodeIDs,
      projectName,
      isPrivate,
      signature,
    }

    const path = `/SCManager?${genUrlParamsFromObject(_request)}`
    const url = `${this.baseUrl + path}${
      path.includes('?') ? '&' : '?'
    }pubKey=${this.sm2Key.publicKey}`
    const sig = sm2.doSignature(
      url.substring(url.indexOf('?') + 1),
      this.sm2Key.privateKey,
      {
        hash: true,
        der: true,
      },
    )
    const source = new EventSource(
      `${this.baseUrl}${path}&pubKey=${this.sm2Key.publicKey}&sign=${sig}`,
    )
    source.addEventListener(
      'message',
      function (_event) {
        // const data = event.data as DistributeContractResponse[]
        // console.log(data, 'dd')
        // handle message
      },
      false,
    )
  }

  saveFile(_request: SaveFileRequest): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject({
          action: 'saveFile',
          ..._request,
        })}`,
      ),
    )
  }

  listProjectPermission(
    _request: ListProjectPermissionRequest,
  ): Promise<ClientResponse<ListProjectPermissionResponseData>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject({
          action: 'listProjectPermission',
          ..._request,
        })}`,
      ),
    )
  }

  startContractMultiPoint(
    peersID: string,
    type: number,
    selectUnitNum: number,
    projectName: string,
    isPrivate: boolean,
    sponsorPeerID: string,
  ): Promise<ClientResponse<string>> {
    const _request = {
      // peerID是节点的公钥,以逗号间隔开
      peersID,
      type,
      selectUnitNum,
      projectName,
      isPrivate,
      sponsorPeerID,
    }
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject({
          action: 'startContractMultiPoint',
          ..._request,
        })}`,
      ),
    )
  }

  // 获取节点配置信息
  async loadNodeConfig(): Promise<ClientResponse<LoadNodeConfigResponseData>> {
    const res = await this.retryAsync(() =>
      this.requestWithSignature<string>(
        `/SCManager?${genUrlParamsFromObject({
          action: 'loadNodeConfig',
        })}`,
      ),
    )
    return {
      ...res,
      data: (res.status === 200 && res.data
        ? (JSON.parse(res.data) as ClientResponse<LoadNodeConfigResponseData>)
            .data
        : {}) as LoadNodeConfigResponseData,
    }
  }

  // 支持的key包括：{licence，projectDir，yjsPath，dataChain，doipConfig，nodeCenter，nodeName，masterAddress，resetNodeCenterWS}
  async updateConfig(
    key: string,
    val: string,
  ): Promise<ClientResponse<boolean>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject({
          action: 'updateConfig',
          key,
          val,
        })}`,
      ),
    )
  }

  // 设置pubkey为node manager
  async resetNodeManager(): Promise<boolean> {
    const res = await this.retryAsync(() =>
      this.requestWithSignature<string>(
        `/SCManager?${genUrlParamsFromObject({
          action: 'resetNodeManager',
        })}`,
      ),
    )
    if (!res.data) {
      return false
    }
    const parsed = JSON.parse(res.data) as { data: string }
    return parsed.data === 'success'
  }

  // 锁定某个用户的的私有目录编辑功能
  lockEdit(): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject({
          action: 'lockEdit',
        })}`,
      ),
    )
  }

  // 解锁某个用户的的私有目录编辑功能
  unlockEdit(): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject({
          action: 'unlockEdit',
        })}`,
      ),
    )
  }

  addNode(nodePubKey: string): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `?${genUrlParamsFromObject({
          action: 'addNode',
          nodePubKey,
        })}`,
      ),
    )
  }

  // 申请角色
  applyRole(role: string): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `?${genUrlParamsFromObject({
          action: 'applyRole',
          role,
        })}`,
      ),
    )
  }

  authNodeManager(
    isAccept: boolean,
    authorizedPubKey: string,
  ): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `?${genUrlParamsFromObject({
          action: 'authNodeManager',
          isAccept,
          authorizedPubKey,
        })}`,
      ),
    )
  }

  listAllUsers(): Promise<ClientResponse<ListAllUsersResponseData>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `?${genUrlParamsFromObject({
          action: 'listAllUsers',
        })}`,
      ),
    )
  }

  // 查看节点列表
  listNodes(): Promise<ListNodesResponse> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `?${genUrlParamsFromObject({
          action: 'listNodes',
        })}`,
      ),
    ) as unknown as Promise<ListNodesResponse>
  }

  // 建立可信执行集群
  async createTrustUnit(
    data: { nodeName: string; pubkey: string }[],
    Msg: string,
  ): Promise<{ action: string; status: string }> {
    const toSign = `action=createTrustUnit&data=${JSON.stringify(
      data,
    )}&msg=${Msg}&pubKey=04303718771b9323c204e607639f14469f9a94e55b0964a408ad3b3864b0493b645d7070da0d550f0c54b934275a8e88dedc3024467b0566db5c1108b1baeaae27`
    const sig = sm2.doSignature(toSign, this.sm2Key.privateKey, {
      hash: true,
      der: true,
    })

    const body = {
      action: 'createTrustUnit',
      data,
      msg: Msg,
      pubKey:
        '04303718771b9323c204e607639f14469f9a94e55b0964a408ad3b3864b0493b645d7070da0d550f0c54b934275a8e88dedc3024467b0566db5c1108b1baeaae27',
      sign: sig,
    }
    return (await this.retryAsync(() =>
      this.requestWithSignature('', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }),
    )) as unknown as { action: string; status: string }
  }

  // 查看可信执行集群列表
  listTrustUnits(): Promise<ClientResponse<{ key: string; value: string }[]>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `?${genUrlParamsFromObject({
          action: 'listTrustUnits',
        })}`,
      ),
    )
  }

  // listContractProcess   查询合约实例列表
  listContractProcess(): Promise<ClientResponse<string>> {
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/SCManager?${genUrlParamsFromObject({
          action: 'listContractProcess',
        })}`,
      ),
    )
  }

  // 下载合约
  downloadContract(
    projectName: string,
    isPrivate: boolean,
    timestamp: number,
  ): Promise<ClientResponse<string>> {
    const _request = {
      action: 'downloadContract',
      projectName,
      isPrivate,
      timestamp,
    }
    return this.retryAsync(() =>
      this.requestWithSignature(
        `/CMManager?${genUrlParamsFromObject(_request)}`,
      ),
    )
  }

  // 配置合约引擎节点，若节点没有设置过node manager，将当前key设置为node manager
  async configNode(args: ConfigNodeArgs): Promise<boolean> {
    if (!(await this.resetNodeManager())) {
      return false
    }

    const kvs: [string, string][] = Object.entries(args).filter(
      ([_k, v]) => v,
    ) as [string, string][]

    await Promise.all([
      ...kvs.map(([k, v]) => this.updateConfig(k, v)),
      this.applyNodeRole('ContractProvider'),
      this.applyNodeRole('ContractUser'),
      this.applyNodeRole('ContractInstanceManager'),
      this.authNodeRole(true, this.sm2Key.publicKey),
    ])

    const config = (await this.loadNodeConfig()).data
    if (!config) {
      return false
    }

    return kvs.every(([k, v]) => config[remapNodeConfigKey(k)] === v)
  }
}

function remapNodeConfigKey(k: string): string {
  switch (k) {
    case 'dataChain':
      return 'bdledger'
    default:
      return k
  }
}
